// Custom hooks for weather digest alerts API calls
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { loadWeatherDigestAlerts, loadExtremeWeatherAlerts } from '../api';

const ONE_HOUR_IN_MS = 60 * 60 * 1000;

/**
 * Custom hook to fetch weather digest alerts for multiple commodities
 * Fetches alerts in parallel using useQueries and aggregates results
 *
 * @param {string[]} commodities - Array of commodity names to fetch alerts for
 * @returns {Object} - { alerts, isLoading, isError, refetch }
 */
export const useWeatherDigestAlerts = (commodities = []) => {
  // Use useTranslation for reactive language changes
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  // Create queries for each commodity
  const queries = useQueries({
    queries: commodities.map((commodity) => ({
      queryKey: ['weatherDigestAlerts', commodity, language],
      queryFn: () => loadWeatherDigestAlerts(commodity, language),
      enabled: !!commodity,
      staleTime: ONE_HOUR_IN_MS,
      gcTime: ONE_HOUR_IN_MS,
    })),
  });

  // Extract stable values from queries for memoization
  const queryDataList = queries.map((q) => q.data);
  const queryErrorList = queries.map((q) => q.isError);

  // Aggregate results from all queries
  const aggregatedData = useMemo(() => {
    const allAlerts = [];
    let hasError = false;

    queryDataList.forEach((data, index) => {
      if (queryErrorList[index]) {
        hasError = true;
      }
      if (data && Array.isArray(data)) {
        allAlerts.push(...data);
      }
    });

    // Sort by iso_year desc, then iso_week desc
    allAlerts.sort((a, b) => {
      // First compare by year (descending)
      const yearDiff = (b.iso_year || 0) - (a.iso_year || 0);
      if (yearDiff !== 0) return yearDiff;
      // Then compare by week (descending)
      return (b.iso_week || 0) - (a.iso_week || 0);
    });

    return {
      alerts: allAlerts,
      isError: hasError,
    };
  }, [JSON.stringify(queryDataList), JSON.stringify(queryErrorList)]);

  // Check if any query is loading
  const isLoading = queries.some((query) => query.isLoading || query.isFetching);

  // Refetch all queries
  const refetch = () => {
    queries.forEach((query) => query.refetch());
  };

  return {
    alerts: aggregatedData.alerts,
    isLoading,
    isError: aggregatedData.isError,
    refetch,
  };
};

/**
 * Custom hook to fetch extreme weather alerts for multiple commodities
 * Fetches alerts in parallel using useQueries and aggregates results
 *
 * @param {string[]} commodities - Array of commodity names to fetch alerts for
 * @returns {Object} - { alerts, isLoading, isError, refetch }
 */
export const useExtremeWeatherAlerts = (commodities = []) => {
  // Use useTranslation for reactive language changes
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  // Create queries for each commodity
  const queries = useQueries({
    queries: commodities.map((commodity) => ({
      queryKey: ['extremeWeatherAlerts', commodity, language],
      queryFn: () => loadExtremeWeatherAlerts(commodity, language),
      enabled: !!commodity,
      staleTime: ONE_HOUR_IN_MS,
      gcTime: ONE_HOUR_IN_MS,
    })),
  });

  // Extract stable values from queries for memoization
  const queryDataList = queries.map((q) => q.data);
  const queryErrorList = queries.map((q) => q.isError);

  // Aggregate results from all queries
  const aggregatedData = useMemo(() => {
    const allAlerts = [];
    const seenAlertIds = new Set();
    let hasError = false;

    queryDataList.forEach((data, index) => {
      if (queryErrorList[index]) {
        hasError = true;
      }
      if (data && Array.isArray(data)) {
        // Deduplicate alerts based on a unique identifier
        data.forEach(alert => {
          const alertId = `${alert.country}-${alert.specific_alert_type}-${alert.region || 'all'}`;
          if (!seenAlertIds.has(alertId)) {
            seenAlertIds.add(alertId);
            allAlerts.push(alert);
          }
        });
      }
    });

    // Sort by status (Active first), then by market_impact (high first)
    const impactOrder = { high: 0, alto: 0, medium: 1, medio: 1, low: 2, bajo: 2 };
    allAlerts.sort((a, b) => {
      // Active alerts first
      if (a.status === 'Active' && b.status !== 'Active') return -1;
      if (a.status !== 'Active' && b.status === 'Active') return 1;
      // Then by impact
      const aImpact = impactOrder[a.market_impact?.toLowerCase()] ?? 3;
      const bImpact = impactOrder[b.market_impact?.toLowerCase()] ?? 3;
      return aImpact - bImpact;
    });

    return {
      alerts: allAlerts,
      isError: hasError,
    };
  }, [JSON.stringify(queryDataList), JSON.stringify(queryErrorList)]);

  // Check if any query is loading
  const isLoading = queries.some((query) => query.isLoading || query.isFetching);

  // Refetch all queries
  const refetch = () => {
    queries.forEach((query) => query.refetch());
  };

  return {
    alerts: aggregatedData.alerts,
    isLoading,
    isError: aggregatedData.isError,
    refetch,
  };
};
